import { FrownOutlined } from '@ant-design/icons'
import { Button, Result, Typography } from 'antd'
import { NavLink, type To, useNavigate } from 'react-router-dom'
import { ZodError } from 'zod'

import { ApiError, NetworkError, NotFoundError } from '../../api'
import { ROUTES } from '../../config'
import { PrimaryButton } from '../PrimaryButton'

const { Paragraph, Text } = Typography

interface ErrorConfig {
  status?: 'error' | 'warning' | 'info' | '403' | '404' | '500'
  title?: string
  subTitle?: string
  icon?: React.ReactNode
  extra?: React.ReactNode
  showRetry?: boolean
  showBack?: boolean
  customAction?: {
    label: React.ReactNode
    onClick: () => void
  }
}
interface NotFoundConfig extends Omit<ErrorConfig, 'status' | 'title'> {
  navigateTo?: To
  buttonLabel?: React.ReactNode
}
interface ErrorResultProps {
  error?: unknown
  preset?: 'notFound'
  notFoundConfig?: NotFoundConfig
  customConfig?: ErrorConfig
  onRetry?: () => void
}

// Хук для получения конфигурации ошибки
function useErrorConfig(
  error: unknown,
  notFoundConfig: NotFoundConfig | undefined,
  customConfig: ErrorConfig | undefined,
  onRetry: (() => void) | undefined,
): ErrorConfig | null {
  const navigate = useNavigate()

  // NotFound ошибки
  if (
    error instanceof NotFoundError ||
    (error instanceof ApiError && error.code === 404) ||
    customConfig?.status === '404'
  ) {
    return {
      status: '404',
      title: '404',
      icon: <FrownOutlined />,
      subTitle: notFoundConfig?.subTitle ?? 'Данной страницы не существует',
      extra: notFoundConfig?.extra ?? (
        <PrimaryButton
          onClick={() => {
            if (notFoundConfig?.navigateTo) {
              void navigate(notFoundConfig.navigateTo, { replace: true })
            } else {
              void navigate(-1)
            }
          }}
        >
          {notFoundConfig?.buttonLabel ?? 'Назад'}
        </PrimaryButton>
      ),
    }
  }

  // Ошибки валидации Zod
  if (error instanceof ZodError) {
    return {
      status: 'error',
      title: 'Ошибка валидации ответа от сервера',
      subTitle:
        'Ответ сервера не соответствует ожидаемой структуре. Пожалуйста, сообщите об этом разработчикам.',
      extra: (
        <div style={{ textAlign: 'left', maxHeight: 300, overflowY: 'auto' }}>
          <Paragraph>
            <Text strong>Детали ошибки:</Text>
          </Paragraph>
          {error.issues.map((issue, index) => (
            <Paragraph key={index}>
              <Text type="danger">
                - [{issue.path.join('.') || 'root'}]: {issue.message}
              </Text>
            </Paragraph>
          ))}
        </div>
      ),
    }
  }

  // Специальные API ошибки (500)
  if (error instanceof ApiError && error.code === 500) {
    return {
      status: '500',
      title: String(error.code),
      subTitle: error.message,
    }
  }

  if (
    (error instanceof ApiError && error.code === 403) ||
    customConfig?.status === '403'
  ) {
    return {
      status: '403',
      title: 'Отсутствуют права доступа',
      subTitle:
        error instanceof ApiError ? error.message : customConfig?.subTitle,
      extra: (
        <NavLink to={ROUTES.MAIN.ROOT}>
          <PrimaryButton>На главную</PrimaryButton>
        </NavLink>
      ),
    }
  }

  // Обычные API ошибки
  if (error instanceof ApiError) {
    return {
      status: 'error',
      title: error.message,
      subTitle: `Код ошибки: ${error.code}`,
    }
  }

  // Сетевые ошибки
  if (error instanceof NetworkError) {
    return {
      status: 'error',
      title: 'Ошибка сети',
      subTitle: error.message,
      extra: (
        <Button onClick={onRetry || (() => window.location.reload())}>
          Попробовать снова
        </Button>
      ),
    }
  }

  // Обычные ошибки
  if (error instanceof Error) {
    return {
      status: 'error',
      title: error.message,
      subTitle:
        'Произошла неизвестная ошибка. Пожалуйста, сообщите об этом разработчикам.',
    }
  }

  return null
}

export function ErrorResult({
  error,
  notFoundConfig,
  customConfig,
  onRetry,
}: ErrorResultProps) {
  const config = useErrorConfig(error, notFoundConfig, customConfig, onRetry)
  const extraConfig: ErrorConfig =
    customConfig && customConfig.customAction
      ? {
          extra: (
            <PrimaryButton onClick={customConfig.customAction.onClick}>
              {customConfig.customAction.label}
            </PrimaryButton>
          ),
        }
      : {}
  const _config = { ...config, ...customConfig, ...extraConfig }
  if (!config) {
    return null
  }

  return (
    <Result
      status={_config.status}
      icon={_config.icon}
      title={_config.title}
      subTitle={_config.subTitle}
      extra={_config.extra}
    />
  )
}
